import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Save, X } from "lucide-react";

const BLANK = {
  name: "",
  tier: "Supporter",
  website: "",
  contact: "",
  logo_url: "",
  active: true,
  sort_order: 0,
};

export default function SponsorsPanel({ client }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // sponsor id or "new"
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/admin/sponsors");
      setRows(data);
    } catch {
      toast.error("Failed to load sponsors");
    }
  }, [client]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setForm({ ...BLANK, sort_order: (rows.at(-1)?.sort_order || 0) + 10 });
    setEditing("new");
  };

  const startEdit = (row) => {
    setForm({
      name: row.name || "",
      tier: row.tier || "Supporter",
      website: row.website || "",
      contact: row.contact || "",
      logo_url: row.logo_url || "",
      active: row.active !== false,
      sort_order: row.sort_order || 0,
    });
    setEditing(row.id);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Sponsor name is required");
      return;
    }
    setBusy(true);
    try {
      if (editing === "new") {
        await client.post("/admin/sponsors", form);
      } else {
        await client.patch(`/admin/sponsors/${editing}`, form);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this sponsor?")) return;
    try {
      await client.delete(`/admin/sponsors/${id}`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleActive = async (row) => {
    try {
      await client.patch(`/admin/sponsors/${row.id}`, { active: !row.active });
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="sponsors-panel">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
          {rows.length} sponsor{rows.length === 1 ? "" : "s"}
        </div>
        <button
          data-testid="sponsors-add-btn"
          onClick={startNew}
          className="flex items-center gap-2 border-2 border-[#1d4ed8] bg-[#1d4ed8] px-3 py-1.5 font-heading text-xs tracking-wider text-white hover:bg-[#1e40af]"
        >
          <Plus className="h-3.5 w-3.5" /> ADD SPONSOR
        </button>
      </div>

      {editing && (
        <div className="border-2 border-[#1d4ed8] bg-white p-4 space-y-3" data-testid="sponsor-editor">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name *">
              <input
                data-testid="sponsor-name-input"
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full border border-slate-300 px-3 py-2"
              />
            </Field>
            <Field label="Tier">
              <select
                data-testid="sponsor-tier-input"
                value={form.tier}
                onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value }))}
                className="w-full border border-slate-300 px-3 py-2 bg-white"
              >
                <option>Platinum</option>
                <option>Gold</option>
                <option>Silver</option>
                <option>Bronze</option>
                <option>Supporter</option>
              </select>
            </Field>
            <Field label="Website">
              <input
                data-testid="sponsor-website-input"
                type="url"
                placeholder="https://…"
                value={form.website}
                onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                className="w-full border border-slate-300 px-3 py-2"
              />
            </Field>
            <Field label="Contact (email/phone)">
              <input
                data-testid="sponsor-contact-input"
                type="text"
                value={form.contact}
                onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))}
                className="w-full border border-slate-300 px-3 py-2"
              />
            </Field>
            <Field label="Logo URL or Upload">
              <input
                data-testid="sponsor-logo-input"
                type="url"
                placeholder="https://…/logo.png"
                value={form.logo_url}
                onChange={(e) => setForm((f) => ({ ...f, logo_url: e.target.value }))}
                className="w-full border border-slate-300 px-3 py-2"
              />
              <div className="mt-2 flex items-center gap-2">
                <input
                  data-testid="sponsor-logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 500_000) {
                      toast.error("Image too big — please pick one under ~500 KB");
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => {
                      setForm((f) => ({ ...f, logo_url: String(reader.result || "") }));
                      toast.success("Logo attached");
                    };
                    reader.readAsDataURL(file);
                  }}
                  className="block text-xs w-full text-slate-600 file:mr-3 file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:font-heading file:text-[10px] file:tracking-wider file:text-white file:hover:bg-slate-700"
                />
                {form.logo_url && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, logo_url: "" }))}
                    className="border border-slate-300 px-2 py-1 text-[10px] uppercase tracking-[0.15em] text-slate-500 hover:border-red-600 hover:text-red-600"
                  >
                    Clear
                  </button>
                )}
              </div>
            </Field>
            <Field label="Sort order">
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))}
                className="w-full border border-slate-300 px-3 py-2 tabular"
              />
            </Field>
          </div>
          {form.logo_url && (
            <div className="border border-slate-200 bg-slate-50 p-3 flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Preview:</span>
              <img
                src={form.logo_url}
                alt=""
                className="h-12 object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              data-testid="sponsor-cancel-btn"
              onClick={() => setEditing(null)}
              className="flex items-center gap-1.5 border-2 border-slate-300 bg-white px-3 py-2 font-heading text-xs tracking-wider text-slate-700 hover:border-slate-500"
            >
              <X className="h-3.5 w-3.5" /> CANCEL
            </button>
            <button
              data-testid="sponsor-save-btn"
              onClick={save}
              disabled={busy}
              className="flex items-center gap-1.5 border-2 border-[#1d4ed8] bg-[#1d4ed8] px-3 py-2 font-heading text-xs tracking-wider text-white hover:bg-[#1e40af] disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" /> {busy ? "SAVING…" : "SAVE"}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.length === 0 && (
          <div className="col-span-full border-2 border-slate-300 bg-white p-8 text-center text-xs uppercase tracking-[0.2em] text-slate-400">
            No sponsors yet. Click "ADD SPONSOR" to get started.
          </div>
        )}
        {rows.map((r) => (
          <div key={r.id} data-testid={`sponsor-row-${r.id}`} className="border-2 border-slate-900 bg-white p-3">
            <div className="flex items-start gap-3">
              {r.logo_url ? (
                <img
                  src={r.logo_url}
                  alt={r.name}
                  className="h-12 w-12 object-contain flex-shrink-0"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <div className="h-12 w-12 flex-shrink-0 bg-slate-100 flex items-center justify-center font-heading text-xl text-slate-400">
                  {r.name?.[0] || "?"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading text-lg text-slate-900 tracking-wider">{r.name}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#1d4ed8]">{r.tier}</span>
                  {!r.active && (
                    <span className="bg-slate-400 px-1.5 py-0.5 text-[9px] tracking-[0.2em] text-white font-heading">
                      HIDDEN
                    </span>
                  )}
                </div>
                {r.website && (
                  <div className="text-xs text-slate-500 truncate">
                    <a href={r.website} target="_blank" rel="noreferrer" className="underline">{r.website}</a>
                  </div>
                )}
                {r.contact && (
                  <div className="text-xs text-slate-500 truncate">{r.contact}</div>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => toggleActive(r)}
                  className="border border-slate-300 px-2 py-1 text-[10px] uppercase tracking-[0.15em] text-slate-500 hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
                  title={r.active ? "Hide from public page" : "Show on public page"}
                >
                  {r.active ? "HIDE" : "SHOW"}
                </button>
                <button
                  data-testid={`sponsor-edit-${r.id}-btn`}
                  onClick={() => startEdit(r)}
                  className="border border-slate-300 p-1.5 text-slate-500 hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
                  title="Edit"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`sponsor-delete-${r.id}-btn`}
                  onClick={() => remove(r.id)}
                  className="border border-slate-300 p-1.5 text-slate-500 hover:border-red-600 hover:text-red-600"
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
